import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useRealtimeWebSocket } from '@/contexts/RealtimeWebSocketContext';
import type { FloorPlanDetail } from '@/lib/api/floorplans';
import {
  createFloorAlarmOrder, floorAlarmMessage, preAlarmToastDuration, preAlarmToastId, preAlarmToastText, preAlarmToastTimestamp, withLocationAlarm,
} from '@/lib/floorAlarm';

/**
 * Staff side of the floor alarm push (`floor_alarm.changed`, sent to the
 * active customer's socket group). Each push names one location (floor,
 * address or room); every cached floor plan that draws it gets that one
 * region updated, so an open plan recolours it at once, and the pre-alarm is
 * toasted. The toast goes when the location leaves pre-alarm. A push older
 * than the location's last one (lower `seq`) is dropped. Renders nothing.
 */
export function FloorAlarmToaster() {
  const { subscribe } = useRealtimeWebSocket();
  const queryClient = useQueryClient();
  useEffect(() => {
    const isNewer = createFloorAlarmOrder();
    return subscribe((raw) => {
      const msg = floorAlarmMessage(raw);
      if (!msg || !isNewer(msg)) return;
      queryClient.setQueriesData<FloorPlanDetail>({ queryKey: ['floorplan'] }, (fp) => withLocationAlarm(fp, msg));
      const id = preAlarmToastId(msg);
      if (msg.state === 'pre_alarm') {
        toast.warning(preAlarmToastText(msg), {
          id,
          duration: preAlarmToastDuration(msg),
          description: preAlarmToastTimestamp(msg),
        });
      } else {
        toast.dismiss(id);
      }
    });
  }, [subscribe, queryClient]);
  return null;
}
